/**
 * Garnet supplier auth — form handlers for login, password recovery, and registration.
 * Loaded on /pages/supplier-login, /pages/supplier-password-reset, /pages/become-a-supplier.
 *
 * Each handler intercepts form submission, posts via fetch, and renders inline status.
 * Error responses from Garnet are documented as { "message": "..." } JSON envelopes.
 */
(function () {
  'use strict';

  var GARNET_BASE = 'https://suppliers.k12learningstore.com';
  var SUPPLIER_DASHBOARD_URL = GARNET_BASE + '/';
  // K12 is set to "Application" in Garnet (Admin Panel > Vendors > Applications).
  // If that changes to "Open registration", switch this to /api3/public/v2/vendors.
  var REGISTRATION_URL = GARNET_BASE + '/api3/public/v2/applications';

  // ---------- Message UI ----------
  // The Liquid markup doesn't include a status element, so we inject one on demand.

  function ensureMessageElement(form) {
    var existing = form.querySelector(':scope > .garnet-form__message');
    if (existing) return existing;
    var el = document.createElement('div');
    el.className = 'garnet-form__message';
    el.hidden = true;
    form.insertBefore(el, form.firstChild);
    return el;
  }

  function showMessage(el, type, text) {
    if (!el) return;
    var isError = type === 'error';
    el.className = 'garnet-form__message alert mb-8 ' +
      (isError ? 'bg-error-bg text-error-text' : 'bg-success-bg text-success-text');
    el.setAttribute('role', isError ? 'alert' : 'status');
    el.textContent = text;
    el.hidden = false;
  }

  function clearMessage(el) {
    if (!el) return;
    el.hidden = true;
    el.textContent = '';
    el.className = 'garnet-form__message';
    el.removeAttribute('role');
  }

  // ---------- Submit state ----------
  // Disable the submit button and swap its label while a request is in flight.

  function setSubmitting(form, submitting) {
    var btn = form.querySelector('button[type="submit"], button:not([type])');
    if (!btn) return;
    if (submitting) {
      if (!btn.dataset.originalText) btn.dataset.originalText = btn.textContent;
      btn.disabled = true;
      btn.textContent = 'Please wait\u2026';
    } else {
      btn.disabled = false;
      if (btn.dataset.originalText) btn.textContent = btn.dataset.originalText;
    }
  }

  // ---------- Response parsing ----------

  // Garnet returns JSON, but parse defensively in case an upstream CDN/proxy returns HTML.
  function parseResponseBody(response) {
    var ct = response.headers.get('content-type') || '';
    if (ct.indexOf('application/json') !== -1) {
      return response.json().catch(function () { return null; });
    }
    return response.text().catch(function () { return ''; });
  }

  // Garnet errors are documented as { "message": "..." }. Anything else falls back.
  function extractErrorMessage(body, fallback) {
    if (body && typeof body === 'object' && typeof body.message === 'string') {
      return body.message;
    }
    return fallback;
  }

  function postForm(form, extras) {
    var body = new URLSearchParams(new FormData(form));
    if (extras) {
      Object.keys(extras).forEach(function (key) { body.set(key, extras[key]); });
    }
    return fetch(form.action, {
      method: 'POST',
      body: body,
      credentials: 'include',
      headers: { 'Accept': 'application/json, text/plain, */*' }
    });
  }

  // ---------- Login ----------
  // On success, the session cookie is set by Garnet — redirect to the dashboard.

  function initLogin() {
    console.log("initLogin");
    var form = document.querySelector('form[action$="/auth/public/login"]');
    if (!form) return;

    form.removeAttribute('target');
    var msg = ensureMessageElement(form);

    // Mirrors a native form POST: x-www-form-urlencoded body, no custom headers,
    // no credentials mode override — keeps the request in the CORS "simple" lane
    // so Garnet doesn't need to send credentialed CORS headers.
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      clearMessage(msg);
      setSubmitting(form, true);
      fetch(form.action, {
        method: 'POST',
        body: new URLSearchParams(new FormData(form))
      }).then(function (response) {
        if (response.ok) {
          window.location.href = SUPPLIER_DASHBOARD_URL;
          return;
        }
        return parseResponseBody(response).then(function (body) {
          var text = extractErrorMessage(body, 'Login failed. Please check your email and password.');
          showMessage(msg, 'error', text);
          setSubmitting(form, false);
        });
      }).catch(function (err) {
        console.error('[garnet-auth] login fetch failed:', err);
        showMessage(msg, 'error', 'Something went wrong. Please try again.');
        setSubmitting(form, false);
      });
    });

    //   postForm(form).then(function (response) {
    //     if (response.ok) {
    //       window.location.href = SUPPLIER_DASHBOARD_URL;
    //       return;
    //     }
    //     return parseResponseBody(response).then(function (body) {
    //       var text = extractErrorMessage(body, 'Login failed. Please check your email and password.');
    //       showMessage(msg, 'error', text);
    //       setSubmitting(form, false);
    //     });
    //   }).catch(function (err) {
    //     console.error('[garnet-auth] login fetch failed:', err);
    //     showMessage(msg, 'error', 'Something went wrong. Please try again.');
    //     setSubmitting(form, false);
    //   });
    // });
  }

  // ---------- Password recovery (two-step) ----------
  // Step 1 requests a reset code by email; step 2 submits the code + new password.
  // After step 2, Garnet sets the session cookie, so we send the user straight to the dashboard.

  function initPasswordRecovery() {
    var requestPanel = document.getElementById('garnet-recover-request');
    var updatePanel = document.getElementById('garnet-recover-update');
    if (!requestPanel || !updatePanel) return;

    var requestForm = requestPanel.querySelector('form');
    var updateForm = updatePanel.querySelector('form');
    if (!requestForm || !updateForm) return;

    requestForm.removeAttribute('target');
    updateForm.removeAttribute('target');

    var requestMsg = ensureMessageElement(requestForm);
    var updateMsg = ensureMessageElement(updateForm);

    requestForm.addEventListener('submit', function (e) {
      e.preventDefault();
      clearMessage(requestMsg);
      setSubmitting(requestForm, true);

      postForm(requestForm).then(function (response) {
        if (response.ok) {
          // Carry the email forward to step 2 and reveal that panel.
          var src = requestPanel.querySelector('input[name="email"]');
          var dst = updatePanel.querySelector('input[name="email"]');
          if (src && dst) dst.value = src.value;
          requestPanel.classList.add('js-hidden');
          updatePanel.classList.remove('js-hidden');
          var code = updatePanel.querySelector('input[name="code"]');
          if (code) code.focus();
          setSubmitting(requestForm, false);
          return;
        }
        return parseResponseBody(response).then(function (body) {
          var text = extractErrorMessage(body, 'Could not send the reset code. Please check the email address and try again.');
          showMessage(requestMsg, 'error', text);
          setSubmitting(requestForm, false);
        });
      }).catch(function (err) {
        console.error('[garnet-auth] recovery request fetch failed:', err);
        showMessage(requestMsg, 'error', 'Something went wrong. Please try again.');
        setSubmitting(requestForm, false);
      });
    });

    updateForm.addEventListener('submit', function (e) {
      console.log(e);
      // e.preventDefault();
      // clearMessage(updateMsg);
      // setSubmitting(updateForm, true);

      // postForm(updateForm).then(function (response) {
      //   if (response.ok) {
      //     // Session cookie is now set — go straight to the dashboard.
      //     showMessage(updateMsg, 'success', 'Password updated. Redirecting\u2026');
      //     setTimeout(function () { window.location.href = SUPPLIER_DASHBOARD_URL; }, 1200);
      //     return;
      //   }
      //   return parseResponseBody(response).then(function (body) {
      //     var text = extractErrorMessage(body, 'Could not update password. Check your code and try again.');
      //     showMessage(updateMsg, 'error', text);
      //     setSubmitting(updateForm, false);
      //   });
      // }).catch(function (err) {
      //   console.error('[garnet-auth] recovery update fetch failed:', err);
      //   showMessage(updateMsg, 'error', 'Something went wrong. Please try again.');
      //   setSubmitting(updateForm, false);
      // });
    });
  }

  // ---------- CSRF ----------
  // Fetched on-demand at submit time. credentials: 'include' is required so the
  // browser stores the session cookie Garnet sets on this response — the
  // subsequent POST needs that cookie to validate the token.

  async function fetchCSRFToken() {
    var res = await fetch(GARNET_BASE + '/api3/public/csrf-token', {
      credentials: 'include',
      headers: { 'x-store': 'k12-homeschool' }
    });
    if (!res.ok) throw new Error('CSRF token request failed: ' + res.status);
    var data = await res.json();
    return data.token;
  }

  // ---------- Registration ----------
  // Fetch a CSRF token on page load (Garnet rejects submits that come too soon after
  // the token is issued), then POST the application as JSON. Garnet has no password
  // step here: once the application is accepted, the vendor is emailed a set-password link.

  // Profile inputs are named profile[<vendor profile metafield key>]. data-name is the
  // question shown in Garnet; fall back to the field's label if it's missing.
  function collectMetafields(form) {
    var metafields = [];
    form.querySelectorAll('[name^="profile["]').forEach(function (input) {
      var value = input.value.trim();
      if (!value) return;
      var key = input.name.slice(8, -1);
      var label = input.id ? form.querySelector('label[for="' + input.id + '"]') : null;
      metafields.push({
        key: key,
        name: input.dataset.name || (label ? label.textContent.trim() : key),
        value: value
      });
    });
    return metafields;
  }

  async function initRegistration() {
    var form = document.getElementById('registrationForm');
    if (!form) return;
    var msg = ensureMessageElement(form);
    var csrfToken = await fetchCSRFToken();

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      clearMessage(msg);
      setSubmitting(form, true);

      var data = new FormData(form);
      fetch(REGISTRATION_URL, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({
          email: (data.get('email') || '').trim(),
          vendor: (data.get('vendor') || '').trim(),
          website: data.get('website') || '',
          csrfToken: csrfToken,
          metafields: collectMetafields(form)
        })
      }).then(function (response) {
        if (response.ok) {
          form.reset();
          showMessage(msg, 'success', 'Thank you! Your application has been submitted. Once it is approved, we will email you a link to set your password.');
          msg.scrollIntoView({ behavior: 'smooth', block: 'center' });
          setSubmitting(form, false);
          return;
        }
        return parseResponseBody(response).then(function (body) {
          var text = extractErrorMessage(body, 'Registration failed. Please review your information and try again.');
          showMessage(msg, 'error', text);
          setSubmitting(form, false);
        });
      }).catch(function (err) {
        console.error('[garnet-auth] registration submit failed:', err);
        showMessage(msg, 'error', 'Something went wrong. Please try again.');
        setSubmitting(form, false);
      });
    });
  }

  // ---------- Init ----------

  function init() {
    // initLogin();
    initPasswordRecovery();
    initRegistration();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();